import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Plus, GripVertical, Trash2, FileText, ClipboardList, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { format, parseISO } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import UserAvatar from '../components/UserAvatar';
import { Link } from 'react-router-dom';
import { FolderOpen } from 'lucide-react';
import { DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DroppableColumn, DraggableCard } from '../components/KanbanDnd';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TasksPage = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, i18n } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [planTasks, setPlanTasks] = useState([]);
  const [projectTasks, setProjectTasks] = useState([]);
  const [expandedDescIds, setExpandedDescIds] = useState({});
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('none');
  const [team, setTeam] = useState('verwaltung');
  const [dueDate, setDueDate] = useState('');
  const [dragId, setDragId] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/tasks`, {
        headers: getAuthHeader()
      });
      // Filter tasks: regular users see only their own tasks or tasks assigned to them
      const filteredTasks = user?.role === 'admin' 
        ? response.data 
        : response.data.filter(t => 
            t.created_by === user?.id || 
            t.assigned_to === user?.id
          );
      setTasks(filteredTasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  }, [getAuthHeader, user?.role, user?.id]);

  const fetchPlanTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/plans/my-tasks`, {
        headers: getAuthHeader()
      });
      setPlanTasks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch plan tasks:', error);
    }
  }, [getAuthHeader]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getAuthHeader]);

  const fetchProjectTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/project-tasks/assigned`, { headers: getAuthHeader() });
      setProjectTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch assigned project tasks:', error);
      toast.error(t('timeCorrection.projectLoadError'));
    }
  }, [getAuthHeader, t]);

  useEffect(() => {
    fetchTasks();
    fetchPlanTasks();
    fetchUsers();
    fetchProjectTasks();
  }, [fetchTasks, fetchPlanTasks, fetchUsers, fetchProjectTasks]);

  useEffect(() => {
    const sync = () => { if (!document.hidden) fetchProjectTasks(); };
    const interval = setInterval(sync, 15000);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [fetchProjectTasks]);

  const toggleDescription = (id) => {
    setExpandedDescIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Map a PlanTask (with completed flag + day) into a kanban "virtual task"
  const planTasksAsKanban = planTasks.map(pt => ({
    id: `plan-${pt.id}`,
    _isPlanTask: true,
    _planId: pt.id,
    _assignedIds: Array.isArray(pt.assigned_to) ? pt.assigned_to : (pt.assigned_to ? [pt.assigned_to] : []),
    title: pt.title,
    description: pt.description,
    team: pt.team,
    status: pt.completed ? 'done' : 'todo',
    priority: pt.priority,
    day: pt.day,
    week_start: pt.week_start,
    assigned_to_name: Array.isArray(pt.assigned_to_names) && pt.assigned_to_names.length > 0
      ? pt.assigned_to_names.join(', ')
      : null,
  }));

  const projectTasksAsKanban = projectTasks.map(task => ({
    ...task,
    id: `project-${task.id}`,
    _isProjectTask: true,
    _projectTaskId: task.id,
    assigned_to: task.assignee_id,
    assigned_to_name: task.assignee_name,
    status: task.completed ? 'done' : 'todo',
  }));
  const allTasks = [...tasks, ...planTasksAsKanban, ...projectTasksAsKanban];

  const changeProjectStatus = async (task, status) => {
    try {
      await axios.put(`${API_URL}/projects/${task.project_id}/tasks/${task._projectTaskId}`,
        { completed: status === 'done' }, { headers: getAuthHeader() });
      await fetchProjectTasks();
    } catch (error) {
      toast.error(t('tasks.statusError'));
      fetchProjectTasks();
    }
  };

  const handleSubmit = async () => {
    if (!title || !team) {
      toast.error(t('tasks.fillRequired'));
      return;
    }

    try {
      await axios.post(
        `${API_URL}/tasks`,
        {
          title,
          description,
          assigned_to: assignedTo === 'none' ? null : assignedTo,
          team,
          due_date: dueDate || null
        },
        { headers: getAuthHeader() }
      );
      toast.success(t('tasks.success'));
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(t('tasks.error'));
    }
  };

  const handleStatusChange = async (taskId, newStatus, isPlanTask = false, planId = null) => {
    try {
      if (isPlanTask) {
        // Plan tasks have a completed boolean only — map todo/in_progress -> false, done -> true
        await axios.put(
          `${API_URL}/plans/tasks/${planId}`,
          { completed: newStatus === 'done' },
          { headers: getAuthHeader() }
        );
        fetchPlanTasks();
      } else {
        await axios.put(
          `${API_URL}/tasks/${taskId}`,
          { status: newStatus },
          { headers: getAuthHeader() }
        );
        fetchTasks();
      }
    } catch (error) {
      toast.error(t('tasks.statusError'));
      if (isPlanTask) fetchPlanTasks(); else fetchTasks();
    }
  };

  const moveTask = (task, status) => {
    const completed = status === 'done';
    if (task._isProjectTask) {
      setProjectTasks((prev) => prev.map((p) => (p.id === task._projectTaskId ? { ...p, completed } : p)));
      changeProjectStatus(task, status);
    } else if (task._isPlanTask) {
      setPlanTasks((prev) => prev.map((p) => (p.id === task._planId ? { ...p, completed } : p)));
      handleStatusChange(task.id, status, true, task._planId);
    } else {
      setTasks((prev) => prev.map((p) => (p.id === task.id ? { ...p, status } : p)));
      handleStatusChange(task.id, status);
    }
  };

  const handleDragEnd = ({ active, over }) => {
    setDragId(null);
    const task = allTasks.find((tk) => tk.id === active.id);
    if (!over || !task || task.status === over.id) return;
    if ((task._isPlanTask || task._isProjectTask) && over.id === 'in_progress') {
      toast.info(t('tasks.noInProgressForLinked'));
      return;
    }
    moveTask(task, over.id);
  };

  const dragTask = dragId ? allTasks.find((tk) => tk.id === dragId) : null;

  const handleDelete = async (taskId) => {
    try {
      await axios.delete(`${API_URL}/tasks/${taskId}`, {
        headers: getAuthHeader()
      });
      toast.success(t('tasks.deleted'));
      fetchTasks();
    } catch (error) {
      toast.error(t('tasks.deleteError'));
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setAssignedTo('none');
    setTeam('verwaltung');
    setDueDate('');
  };

  const getTeamColor = (team) => {
    const colors = {
      marketing: 'bg-[#F3E8FF] text-[#7E22CE]',
      website: 'bg-[#E0F2FE] text-[#0369A1]',
      verwaltung: 'bg-[#FFEDD5] text-[#C2410C]'
    };
    return colors[team] || colors.verwaltung;
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'bg-[#F1F5F9] text-[#334155] border-[#E2E8F0]',
      in_progress: 'bg-[#FEF3C7] text-[#B45309] border-[#FDE68A]',
      done: 'bg-[#D1FAE5] text-[#047857] border-[#A7F3D0]'
    };
    return colors[status] || colors.todo;
  };

  const getStatusLabel = (status) => {
    return t(`tasks.${status}`);
  };

  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const columns = [
    { id: 'todo', label: t('tasks.todo'), color: 'bg-zinc-50/50' },
    { id: 'in_progress', label: t('tasks.inProgress'), color: 'bg-[#FEF3C7]/30' },
    { id: 'done', label: t('tasks.done'), color: 'bg-[#D1FAE5]/30' }
  ];

  return (
    <Layout>
      <div data-testid="tasks-page">
        {/* Header Section - Centered with Button Below */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('tasks.title')}
          </h1>
          <p className="text-zinc-600 mb-1">{t('tasks.subtitle')}</p>
          <p className="text-xs text-zinc-400 mb-6" data-testid="tasks-drag-hint">{t('tasks.dragHint')}</p>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-task-button"
                className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full px-6 py-3 text-sm font-medium shadow-lg hover:shadow-xl transition-all"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('tasks.createTask')}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="task-dialog">
              <DialogHeader>
                <DialogTitle>{t('tasks.newTask')}</DialogTitle>
                <DialogDescription className="sr-only">{t('tasks.newTask')}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="title">{t('tasks.taskTitle')}</Label>
                  <Input
                    id="title"
                    placeholder={t('tasks.taskTitle')}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">{t('tasks.description')}</Label>
                  <Textarea
                    id="description"
                    placeholder={t('tasks.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="description-input"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="team">{t('tasks.team')}</Label>
                  <Select value={team} onValueChange={setTeam}>
                    <SelectTrigger data-testid="team-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="marketing">{t('teams.marketing')}</SelectItem>
                      <SelectItem value="website">{t('teams.website')}</SelectItem>
                      <SelectItem value="verwaltung">{t('teams.verwaltung')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="assigned-to">{t('tasks.assignTo')}</Label>
                  <Select value={assignedTo} onValueChange={setAssignedTo}>
                    <SelectTrigger data-testid="assign-select">
                      <SelectValue placeholder={t('tasks.nobody')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('tasks.nobody')}</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.team})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="due-date">{t('tasks.dueDate')}</Label>
                  <Input
                    id="due-date"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    data-testid="due-date-input"
                  />
                </div>
                <Button
                  onClick={handleSubmit}
                  data-testid="submit-task-button"
                  className="w-full bg-[#D93025] hover:bg-[#B91C1C] text-white"
                >
                  {t('tasks.save')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <DndContext sensors={sensors} onDragStart={({ active }) => setDragId(active.id)} onDragEnd={handleDragEnd} onDragCancel={() => setDragId(null)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="kanban-board">
          {columns.map((column) => (
            <DroppableColumn key={column.id} id={column.id} testId={`drop-column-${column.id}`} className={`${column.color} p-4 rounded-2xl min-w-0`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-zinc-900 uppercase text-xs tracking-wider">
                  {column.label}
                </h2>
                <span className="text-xs text-zinc-600 bg-white px-2 py-1 rounded-full">
                  {allTasks.filter(tk => tk.status === column.id).length}
                </span>
              </div>
              <div className="space-y-3" data-testid={`column-${column.id}`}>
                {allTasks
                  .filter(task => task.status === column.id)
                  .map((task, index) => {
                    const isPlan = task._isPlanTask;
                    const isProject = task._isProjectTask;
                    const isDescOpen = !!expandedDescIds[task.id];
                    return (
                      <DraggableCard key={task.id} id={task.id}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white p-4 rounded-xl border border-zinc-200 shadow-sm hover:shadow-md transition-all group"
                        data-testid={`task-card-${task.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <GripVertical className="w-4 h-4 text-zinc-300 cursor-grab" />
                            {!isProject && <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTeamColor(task.team)}`}>
                              {task.team}
                            </span>}
                            {isProject && <Link to={`/projects/${task.project_id}`}
                              title={task.project_name} data-testid={`project-task-link-${task._projectTaskId}`}
                              className="inline-flex items-center gap-1.5 min-w-0 max-w-full text-xs font-semibold text-emerald-700 hover:text-emerald-900 transition-colors">
                              <FolderOpen className="w-3.5 h-3.5 shrink-0" />
                              <span className="break-all">{task.project_name}</span>
                            </Link>}
                            {isPlan && (
                              <span
                                className="text-[10px] uppercase tracking-wide font-bold bg-[#FEE2E2] text-[#D93025] px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                                title={t('tasks.fromPlanTooltip')}
                                data-testid={`from-plan-badge-${task.id}`}
                              >
                                <ClipboardList className="w-3 h-3" />
                                {t('tasks.fromPlan')}
                              </span>
                            )}
                          </div>
                          {!isPlan && !isProject && (
                            <button
                              onClick={() => handleDelete(task.id)}
                              data-testid={`delete-task-${task.id}`}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <h3 className="font-medium text-zinc-900 mb-2 [overflow-wrap:anywhere]" data-testid={`task-title-${task.id}`}>{task.title}</h3>

                        {/* Description indicator */}
                        {task.description && (
                          <div className="mb-3">
                            <button
                              type="button"
                              onClick={() => toggleDescription(task.id)}
                              data-testid={`task-desc-toggle-${task.id}`}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full transition-colors ${
                                isDescOpen ? 'bg-zinc-100 text-zinc-800' : 'bg-zinc-50 text-zinc-600 hover:bg-zinc-100'
                              }`}
                              aria-expanded={isDescOpen}
                            >
                              <FileText className="w-3.5 h-3.5" />
                              {isDescOpen ? t('tasks.hideDescription') : t('tasks.showDescription')}
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDescOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <AnimatePresence initial={false}>
                              {isDescOpen && (
                                <motion.p
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap"
                                  data-testid={`task-desc-content-${task.id}`}
                                >
                                  {task.description}
                                </motion.p>
                              )}
                            </AnimatePresence>
                          </div>
                        )}

                        {task.assigned_to_name && (
                          <div className="flex items-center gap-2 mb-3">
                            <UserAvatar
                              user={(() => {
                                if (task._isPlanTask && Array.isArray(task._assignedIds) && task._assignedIds.length > 0) {
                                  const uid = task._assignedIds[0];
                                  return users.find(x => x.id === uid) || { id: uid, name: task.assigned_to_name };
                                }
                                const found = users.find(x => x.id === task.assigned_to);
                                return found || { id: task.assigned_to, name: task.assigned_to_name };
                              })()}
                              size="sm"
                            />
                            <span className="text-sm text-zinc-600 truncate">{task.assigned_to_name}</span>
                          </div>
                        )}
                        {!isPlan && task.due_date && (
                          <div className="text-xs text-zinc-500 mb-3">
                            {t('tasks.due')}: {format(new Date(task.due_date), 'd. MMM yyyy', { locale: getLocale() })}
                          </div>
                        )}
                        {isPlan && task.week_start && (
                          <div className="text-xs text-zinc-500 mb-3">
                            {t('tasks.fromWeek')}: {format(parseISO(task.week_start), 'd. MMM yyyy', { locale: getLocale() })}
                          </div>
                        )}
                        <Select
                          value={task.status}
                          onValueChange={(value) => isProject ? changeProjectStatus(task, value) : handleStatusChange(task.id, value, isPlan, task._planId)}
                        >
                          <SelectTrigger
                            data-testid={`status-select-${task.id}`}
                            className={`text-xs border ${getStatusColor(task.status)}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem data-testid={`task-status-todo-${task.id}`} value="todo">{t('tasks.todo')}</SelectItem>
                            {!isPlan && !isProject && <SelectItem data-testid={`task-status-in-progress-${task.id}`} value="in_progress">{t('tasks.inProgress')}</SelectItem>}
                            <SelectItem data-testid={`task-status-done-${task.id}`} value="done">{t('tasks.done')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </motion.div>
                      </DraggableCard>
                    );
                  })}
                {allTasks.filter(tk => tk.status === column.id).length === 0 && (
                  <div className="text-center text-zinc-400 py-8 text-sm">
                    {t('tasks.noTasks')}
                  </div>
                )}
              </div>
            </DroppableColumn>
          ))}
        </div>
        <DragOverlay>
          {dragTask && (
            <div className="bg-white p-4 rounded-xl border-2 border-[#D93025] shadow-2xl rotate-2 cursor-grabbing" data-testid="drag-overlay-card">
              <div className="flex items-center gap-2 mb-1">
                <GripVertical className="w-4 h-4 text-zinc-400" />
                {!dragTask._isProjectTask && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTeamColor(dragTask.team)}`}>{dragTask.team}</span>}
              </div>
              <h3 className="font-medium text-zinc-900 [overflow-wrap:anywhere]">{dragTask.title}</h3>
            </div>
          )}
        </DragOverlay>
        </DndContext>
      </div>
    </Layout>
  );
};

export default TasksPage;
